import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { useEvent } from '../store';
import { loadEvent as loadEventFromStorage, mergeTemplateIntoEvent } from '../utils';
import { useEventPolling } from '../hooks';
import { formatCurrency } from '../utils';

/**
 * Screen Cast Page
 * Optimized for both mobile and TV viewing
 * No navigation elements - pure display view
 */
export function ScreenCastPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { event, loadEvent, leaderboard, fights, bets } = useEvent();
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Handle fullscreen with mobile browser support
  const toggleFullscreen = () => {
    const elem = document.documentElement;
    
    if (!document.fullscreenElement && 
        !(document as any).webkitFullscreenElement && 
        !(document as any).mozFullScreenElement) {
      // Try standard fullscreen API
      if (elem.requestFullscreen) {
        elem.requestFullscreen().catch((err) => {
          console.error('Error entering fullscreen:', err);
        });
      }
      // Fallback for Safari/iOS
      else if ((elem as any).webkitRequestFullscreen) {
        (elem as any).webkitRequestFullscreen();
      }
      // Fallback for older Firefox
      else if ((elem as any).mozRequestFullScreen) {
        (elem as any).mozRequestFullScreen();
      }
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      } else if ((document as any).mozCancelFullScreen) {
        (document as any).mozCancelFullScreen();
      }
    }
  };

  // Listen for fullscreen changes (all browser prefixes)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(
        !!document.fullscreenElement || 
        !!(document as any).webkitFullscreenElement || 
        !!(document as any).mozFullScreenElement
      );
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Load event from Firestore
  useEffect(() => {
    if (!eventId) {
      navigate('/');
      return;
    }

    const loadEventData = async () => {
      try {
        let eventData = await loadEventFromStorage(eventId);
        if (eventData) {
          eventData = await mergeTemplateIntoEvent(eventData);
          loadEvent(eventData);
        } else {
          navigate('/');
        }
      } catch (err) {
        console.error('Error loading event:', err);
        navigate('/');
      }
    };

    loadEventData();
  }, [eventId, loadEvent, navigate]);

  // Poll for event updates (every 5 seconds)
  useEventPolling({
    eventId: eventId || null,
    enabled: !!eventId,
    interval: 5000,
    onUpdate: (_updatedEvent) => {
      console.log('Event updated via polling (screen cast)');
    },
    onError: (error) => {
      console.error('Error polling event:', error);
    },
  });

  if (!event) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-secondary-text text-base md:text-xl">Loading...</p>
        </div>
      </div>
    );
  }

  // Get top 5 leaderboard entries
  const top5 = leaderboard.slice(0, 5);

  // Get current/next fight
  // Fights are already in card order (first = opening fight, last = main event)
  // Find the first live/in-progress fight, or the first upcoming fight
  const currentFight = fights.find((f) => f.status === 'live' || f.status === 'in-progress') || 
                       fights.find((f) => f.status === 'upcoming');
  
  // Get betting summary for current fight (only pending bets)
  const fightBets = currentFight ? bets.filter((b) => 
    (b.fightId === currentFight.id || (b.isParlay && b.parlayLegs?.some(leg => leg.fightId === currentFight.id))) 
    && b.status === 'pending'
  ) : [];
  
  // Calculate per-participant totals for this fight
  const participantBetTotals = new Map<string, { regularAmount: number; parlayAmount: number; name: string }>();
  
  fightBets.forEach(bet => {
    const participant = event.participants.find(p => p.id === bet.participantId);
    if (!participant) return;
    
    const existing = participantBetTotals.get(bet.participantId) || { regularAmount: 0, parlayAmount: 0, name: participant.name };
    
    if (bet.isParlay) {
      existing.parlayAmount += bet.amount;
    } else if (bet.fightId === currentFight?.id) {
      existing.regularAmount += bet.amount;
    }
    
    participantBetTotals.set(bet.participantId, existing);
  });
  
  // Sort by total amount descending
  const sortedParticipantBets = Array.from(participantBetTotals.entries())
    .map(([_id, data]) => ({
      name: data.name,
      regularAmount: data.regularAmount,
      parlayAmount: data.parlayAmount,
      totalAmount: data.regularAmount + data.parlayAmount
    }))
    .sort((a, b) => b.totalAmount - a.totalAmount);
  
  const totalBetAmount = fightBets.reduce((sum, bet) => sum + bet.amount, 0);

  return (
    <div className="min-h-screen bg-primary-bg p-2 sm:p-3 md:p-4">
      <div className="max-w-full mx-auto">
        {/* Compact Header with Event Name and Next Fight */}
        <div className="mb-2 sm:mb-3 flex items-start gap-2 sm:gap-3">
          {/* Event Name Box */}
          <div className="flex-1 min-w-0 bg-secondary-bg border border-border rounded-lg p-2 sm:p-3">
            <h1 className="text-sm sm:text-base md:text-lg font-bold text-primary-text truncate">
              {event.name}
            </h1>
            <p className="text-xs text-secondary-text truncate">
              {new Date().toLocaleDateString('en-US', { 
                weekday: 'short', 
                month: 'short', 
                day: 'numeric' 
              })}
            </p>
          </div>

          {/* Next Fight Box */}
          <div className="flex-1 min-w-0 bg-secondary-bg border border-border rounded-lg p-2 sm:p-3">
            {!currentFight ? (
              <div className="text-sm text-secondary-text">No upcoming fights</div>
            ) : (
              <>
                <div className="text-xs text-secondary-text mb-0.5">
                  {currentFight?.status === 'live' ? '🔴 LIVE NOW' : 'Next Fight'}
                </div>
                <div className="text-sm sm:text-base md:text-lg font-bold text-primary-text truncate">
                  {currentFight.fighters[0]?.name || 'TBD'} vs {currentFight.fighters[1]?.name || 'TBD'}
                </div>
              </>
            )}
          </div>

          {/* Fullscreen Button - Desktop Only */}
          <button
            onClick={toggleFullscreen}
            className="hidden md:flex px-3 py-2 bg-secondary-bg hover:bg-tertiary-bg border border-border rounded-lg text-primary-text text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0"
            title={isFullscreen ? 'Exit Fullscreen (ESC)' : 'Enter Fullscreen'}
          >
            {isFullscreen ? '⛶ Exit' : '⛶ Full'}
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
          {/* Left Column: Top 5 Leaderboard */}
          <div className="bg-secondary-bg rounded-lg p-3 sm:p-4 md:p-5 lg:p-6 border border-border">
            <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-primary-text mb-2 sm:mb-3 md:mb-4">
              Top 5 Leaderboard
            </h2>
            
            {top5.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm md:text-base lg:text-xl text-secondary-text">No participants yet</p>
              </div>
            ) : (
              <div className="space-y-1.5 md:space-y-2">
                {top5.map((entry) => {
                  const getRankDisplay = (rank: number) => {
                    if (rank === 1) return '🥇';
                    if (rank === 2) return '🥈';
                    if (rank === 3) return '🥉';
                    return `#${rank}`;
                  };

                  const getRankColor = (rank: number) => {
                    if (rank === 1) return 'text-gold';
                    if (rank === 2) return 'text-secondary-text';
                    if (rank === 3) return 'text-warning-yellow';
                    return 'text-primary-text';
                  };

                  // Get participant for detailed stats
                  const participant = event.participants.find(p => p.id === entry.participantId);
                  const totalRisked = participant?.bets.reduce((sum, bet) => sum + bet.amount, 0) || 0;
                  const totalLost = participant?.bets.filter(b => b.status === 'lost').reduce((sum, bet) => sum + bet.amount, 0) || 0;
                  const bank = participant?.totalAvailable || 0;

                  return (
                    <div
                      key={entry.participantId}
                      className="bg-tertiary-bg rounded-lg p-2 md:p-3 border border-border"
                    >
                      <div className="flex items-center justify-between gap-2 md:gap-3">
                        <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
                          {/* Rank */}
                          <div className={`text-xl md:text-2xl font-bold ${getRankColor(entry.rank)} flex-shrink-0`}>
                            {getRankDisplay(entry.rank)}
                          </div>

                          {/* Participant Info */}
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm md:text-base lg:text-lg font-bold text-primary-text truncate leading-tight">
                              {entry.participantName}
                            </h3>
                            <div className="flex items-center gap-2 md:gap-3 text-xs text-secondary-text">
                              <span>Risked: {formatCurrency(totalRisked)}</span>
                              <span>Lost: {formatCurrency(totalLost)}</span>
                              <span>Bank: {formatCurrency(bank)}</span>
                            </div>
                          </div>
                        </div>

                        {/* Profit */}
                        <div className="text-right flex-shrink-0">
                          <div className="text-xs text-secondary-text">Profit</div>
                          <div className={`text-base md:text-lg lg:text-xl font-bold ${
                            entry.winningsBalance > 0 ? 'text-success-green' : 
                            entry.winningsBalance === 0 ? 'text-secondary-text' : 
                            'text-error-red'
                          }`}>
                            {formatCurrency(entry.winningsBalance)}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Right Column: Betting Summary */}
          <div className="bg-secondary-bg rounded-lg p-3 sm:p-4 md:p-5 lg:p-6 border border-border">
            {!currentFight ? (
              <div className="text-center py-8">
                <p className="text-sm sm:text-base md:text-lg text-secondary-text">No fights to show</p>
              </div>
            ) : (
              <div>
                {/* Betting Summary - Per Participant */}
                <div>
                  <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-primary-text mb-2 sm:mb-3">
                    Bets on This Fight
                  </h3>
                  {sortedParticipantBets.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-sm md:text-base lg:text-xl text-secondary-text">No bets yet</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-64 lg:max-h-80 overflow-y-auto">
                      {sortedParticipantBets.map((entry, index) => {
                        let displayText = `${entry.name} risks ${formatCurrency(entry.totalAmount)}`;
                        if (entry.parlayAmount > 0) {
                          displayText += ` (${formatCurrency(entry.parlayAmount)} in parlay)`;
                        }
                        
                        return (
                          <div
                            key={index}
                            className="flex items-center justify-between text-sm md:text-base lg:text-lg p-2 md:p-3 bg-primary-bg rounded border border-border"
                          >
                            <span className="text-primary-text truncate">{displayText}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  <div className="mt-3 pt-3 border-t border-border">
                    <div className="flex justify-between text-base md:text-lg lg:text-xl">
                      <span className="text-secondary-text font-bold">Total at Risk:</span>
                      <span className="text-primary-text font-bold">{formatCurrency(totalBetAmount)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
